import { RoboqlQuery } from "@/shared/domain/query";
import {
  fromSavedFilters,
  toSavedFilters,
  VIEW_SCHEME_V1,
  type SavedFilters,
  type ViewDefinitionV1,
  type ViewDisplay,
} from "@/shared/domain/views";
import type { DataTableSort } from "@/shared/foundation/primitives/Table";
import type { SortDirection } from "@/types";

import type { ActiveFiltersState, Filter, FilterMatchMode } from "./Filters";

/**
 * The live state of a resource view — what this module reads on save.
 *
 * "View state" is the resource view's state; a *View* is the saved artifact.
 * Assembled from three owners: filters, table sort/page size, and grid columns.
 */
export interface ResourceViewState {
  filters: ActiveFiltersState;
  sort: DataTableSort | null;
  pageSize: number;
  visibleColumns: string[];
}

/**
 * What a saved View restores to.
 *
 * Not `ResourceViewState`: a RoboQL View restores its text but not the
 * transpiled spec, which only the server can produce. The caller resolves it
 * before handing filter state to the view.
 */
export interface RestoredViewState {
  filters: RestoredFilters;
  sort: DataTableSort | null;
  /** `null` when the View recorded no preference; apply the table's own default. */
  pageSize: number | null;
  /** `null` when the View recorded no preference; apply the table's own defaults. */
  visibleColumns: string[] | null;
}

/**
 * Filter state as restored from a View. The `"roboql"` arm carries the query
 * alone — the missing `spec` is what forces consumers to convert before
 * searching.
 */
export type RestoredFilters =
  | { type: "none" }
  | { type: "structured"; filters: Filter[]; matchMode: FilterMatchMode }
  | { type: "roboql"; query: RoboqlQuery };

/**
 * Capture the current resource view state as a saveable View definition.
 *
 * Records the author's controls, not the query they translate to: translation
 * resolves a relative date to fixed instants, so re-translating on load is what
 * keeps a relative filter relative. A RoboQL View saves only its text.
 */
export function viewStateToDefinition(
  state: ResourceViewState,
): ViewDefinitionV1 {
  return {
    scheme: VIEW_SCHEME_V1,
    roboql:
      state.filters.type === "roboql" ? state.filters.query.toString() : null,
    filters:
      state.filters.type === "structured"
        ? toSavedFilters(
            state.filters.filters.map(toViewWireFilter),
            state.filters.matchMode,
          )
        : null,
    display: {
      visible_columns: state.visibleColumns,
      sort_by: state.sort?.columnId ?? null,
      sort_direction: state.sort ? toWireDirection(state.sort.direction) : null,
      page_size: state.pageSize,
    },
  };
}

/**
 * Restore what a saved View recorded. Page size and columns come back as `null`
 * when the View recorded no preference, so the caller applies its own defaults.
 *
 * @throws If written under an unknown schema — reading it as v1 would apply a
 * filter set the author never chose.
 */
export function definitionToViewState(
  definition: ViewDefinitionV1,
): RestoredViewState {
  if (definition.scheme !== VIEW_SCHEME_V1) {
    throw new Error(
      `Cannot read a View saved as '${definition.scheme}'. This client understands '${VIEW_SCHEME_V1}'.`,
    );
  }

  return {
    filters: readFilters(definition),
    sort: readSort(definition.display),
    pageSize: definition.display.page_size,
    visibleColumns: definition.display.visible_columns.length
      ? definition.display.visible_columns
      : null,
  };
}

function readFilters(definition: ViewDefinitionV1): RestoredFilters {
  // RoboQL first: a definition carrying both is malformed, and the typed query
  // is the better guess at intent.
  if (definition.roboql !== null) {
    return { type: "roboql", query: RoboqlQuery.from(definition.roboql) };
  }
  if (definition.filters !== null) {
    return {
      type: "structured",
      ...restoreFilters(definition.filters),
    };
  }
  return { type: "none" };
}

/**
 * Project `set` and `enum` down to the bare values the wire takes.
 *
 * `roboto.query.SetFilter` and `EnumFilter` declare `values: list[str]` and forbid extra keys,
 * so the `{ value, label }` options the UI holds are refused on save. Only `identity` keeps
 * its labels, deliberately: a principal id is not a name a reader recognises, and unlike these
 * two its label cannot be reconstructed.
 *
 * Local to Views rather than folded into `toSavedFilters`. That helper is shared with saved
 * dashboards, whose read path validates through `savedFiltersSchema` — which declares these
 * values as labeled options. Projecting there would make dashboards write a shape its own
 * reader rejects. The two schemas disagreeing about `set` and `enum` is a real problem, but it
 * belongs to whoever owns that boundary, not to a View serializer.
 */
function toViewWireFilter(filter: Filter): Filter {
  if (filter.type === "set" || filter.type === "enum") {
    // The stored shape is not a `Filter` for these two; the cast marks that
    // boundary rather than hiding it.
    return {
      ...filter,
      values: filter.values.map((option) => option.value),
    } as unknown as Filter;
  }

  return filter;
}

/**
 * Rebuild the options the UI needs from the bare values that were stored.
 *
 * A bare value is its own label, which is what it displayed as when it was picked — these are
 * the option sets whose labels equal their values. Tolerates labeled options too, so a View
 * written before the projection existed still restores.
 */
function restoreFilters(saved: SavedFilters): {
  filters: Filter[];
  matchMode: FilterMatchMode;
} {
  const restored = fromSavedFilters(saved);
  return {
    ...restored,
    filters: restored.filters.map((filter) => {
      if (filter.type !== "set" && filter.type !== "enum") {
        return filter;
      }

      const stored = filter.values as unknown as (
        | string
        | LabeledOptionValue
      )[];
      return {
        ...filter,
        values: stored.map((value) =>
          typeof value === "string" ? { value, label: value } : value,
        ),
      } as unknown as Filter;
    }),
  };
}

/** The `{ value, label }` pair a committed option-backed filter holds. */
interface LabeledOptionValue {
  value: string;
  label: string;
}

function toWireDirection(direction: DataTableSort["direction"]): SortDirection {
  return direction === "desc" ? "DESC" : "ASC";
}

function readSort(display: ViewDisplay): DataTableSort | null {
  if (!display.sort_by) {
    return null;
  }
  return {
    columnId: display.sort_by,
    direction: display.sort_direction === "DESC" ? "desc" : "asc",
  };
}
